import React from 'react';
import { useI18n } from '../../contexts/I18nContext';
import type { ExistingDefault } from './defaultConflict';

interface Props {
  existing: ExistingDefault | null;
  /** The owner's name (ANEP or the client) and the gateway type. */
  ownerName: string;
  deviceType: string;
  /** The template about to take the default flag. */
  newName: string;
  busy?: boolean;
  onReplace: () => void;
  onSaveWithoutDefault: () => void;
  onCancel: () => void;
}

/**
 * Asked before a save that would take the "default template" flag from
 * another template of the same owner and gateway type. Three ways out:
 * replace the default, save with the flag off, or go back to the form.
 */
export const ReplaceDefaultDialog: React.FC<Props> = ({ existing, ownerName, deviceType, newName, busy, onReplace, onSaveWithoutDefault, onCancel }) => {
  const { formatNumber, t } = useI18n();
  if (!existing) return null;
  return (
    <div className="modal-overlay rdd-overlay" onClick={onCancel} role="presentation">
      <div className="rdd" role="alertdialog" aria-modal="true" aria-labelledby="rdd-title" data-testid="replace-default-dialog" onClick={(e) => e.stopPropagation()}>
        <h3 id="rdd-title">{t('configTemplates.replaceDefault.title')}</h3>
        <p>
          {t('configTemplates.replaceDefault.existing', { owner: ownerName, type: deviceType })}{' '}
          <strong>{existing.name}</strong>
          {', '}
          {t('configTemplates.replaceDefault.usedBy', { count: formatNumber(existing.device_count) })}
        </p>
        <p>{t('configTemplates.replaceDefault.consequence', { name: newName })}</p>
        <p className="rdd-note">{t('configTemplates.replaceDefault.devicesKeep', { count: formatNumber(existing.device_count) })}</p>
        <div className="rdd-actions">
          <button type="button" className="btn btn-secondary" data-testid="rdd-cancel" onClick={onCancel} disabled={busy}>{t('common.cancel')}</button>
          <button type="button" className="btn btn-secondary" data-testid="rdd-without" onClick={onSaveWithoutDefault} disabled={busy}>{t('configTemplates.replaceDefault.saveWithout')}</button>
          <button type="button" className="btn btn-primary" data-testid="rdd-replace" onClick={onReplace} disabled={busy}>{t('configTemplates.replaceDefault.replace')}</button>
        </div>
      </div>
    </div>
  );
};
